/**
 * Property Sidebar Component
 *
 * This component provides a sidebar interface for viewing and filtering survey properties
 * in the Interactive Survey Market Map.
 *
 * FEATURES:
 * - Property list with filtering options
 * - Quick filter buttons for shortlisted and hidden properties
 * - Responsive design for desktop and mobile
 * - Property selection and click handling
 * - Loading and empty states
 */

import { useAppSelector } from "@/app/hooks";
import { MapFilters } from "@/features/auth/types";
import { LeaseComp, LeaseCompFieldRule } from "@/features/leaseComps/types";
import { LeaseMediaFiles } from "@/features/leaseMedia/types/leaseMedia.types";
import { Property } from "@/features/survey/types";
import PropertyCard from "@/shared/components/PropertyCard";
import ShortlistFilterToggle from "@/shared/components/ShortlistFilterToggle";
import useTransformedImageUrl from "@/shared/hooks/useTransformedImage";
import { getLeaseCompIconData } from "@/shared/utils/map";
import { EmeraldButton } from "@emerald-react/button";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import placeholderImage from "/image-placeholder-medium.png";

interface LeaseCompCardProps {
  comp: LeaseComp;
  index: number;
  leaseCompFieldRules: LeaseCompFieldRule[];
  leaseMediaFiles: LeaseMediaFiles;
  selectedLeaseCompId?: string | null;
  showPropertyPhoto: boolean;
  isLeaseCompsSectionHidden: boolean;
  surveyId?: string;
  onLeaseCompClick?: (leaseCompId: string) => void;
}

const LeaseCompCard = ({
  comp,
  index,
  leaseCompFieldRules,
  leaseMediaFiles,
  selectedLeaseCompId,
  showPropertyPhoto,
  isLeaseCompsSectionHidden,
  surveyId,
  onLeaseCompClick,
}: LeaseCompCardProps) => {
  const navigate = useNavigate();
  const fields = comp.leaseCompFields;

  const hasFieldRules = leaseCompFieldRules.length > 0;
  const isFieldActive = (keyName: string) =>
    leaseCompFieldRules.some((r) => r.keyName === keyName && r.isActive);

  const nameFieldVisible = hasFieldRules ? isFieldActive("propertyName") : true;
  const addressFieldVisible = hasFieldRules
    ? isFieldActive("propertyAddress")
    : true;
  const cityFieldVisible = hasFieldRules ? isFieldActive("propertyCity") : true;
  const stateFieldVisible = hasFieldRules
    ? isFieldActive("propertyState")
    : true;
  const postalCodeFieldVisible = hasFieldRules
    ? isFieldActive("propertyPostalCode")
    : true;

  const nameValue = comp.name || fields.propertyName;
  const hasRealName =
    !!nameValue &&
    nameValue.trim() !== "" &&
    nameValue !== "Untitled Lease Comp";
  const nameIsTitle = nameFieldVisible && hasRealName;

  const displayTitle = nameIsTitle
    ? nameValue!
    : fields.propertyAddress || "Lease Comp";

  const formatAddress = (): string => {
    const stateZip = [
      stateFieldVisible ? fields.propertyState : null,
      postalCodeFieldVisible ? fields.propertyPostalCode : null,
    ]
      .filter(Boolean)
      .join("  ");
    const cityStateZip = [
      cityFieldVisible ? fields.propertyCity : null,
      stateZip || null,
    ].filter(Boolean);
    if (nameIsTitle) {
      const parts = [
        addressFieldVisible
          ? fields.propertyAddress?.replace(/,+\s*$/, "")
          : null,
        ...cityStateZip,
      ].filter(Boolean);
      return parts.join(", ");
    }
    return cityStateZip.join(", ");
  };

  const rawImageSrc =
    leaseMediaFiles[comp.leaseCompId]?.images?.[0]?.cloudfrontUrl ||
    placeholderImage;

  const { transformedImageUrl } = useTransformedImageUrl(
    rawImageSrc !== placeholderImage ? rawImageSrc : undefined,
  );
  const imageSrc = transformedImageUrl || rawImageSrc;

  const { iconColor } = getLeaseCompIconData(comp);

  return (
    <div
      key={comp.leaseCompId}
      onClick={() => onLeaseCompClick?.(comp.leaseCompId)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onLeaseCompClick?.(comp.leaseCompId);
        }
      }}
      aria-label={`View ${displayTitle} on map`}
    >
      <div
        className="property-card prop-card-row bg-white"
        style={{
          borderColor:
            selectedLeaseCompId === comp.leaseCompId
              ? "var(--color-cbre-green-light)"
              : "transparent",
          borderWidth: "2px",
          borderStyle: "solid",
        }}
      >
        <div className="property-card-details">
          {showPropertyPhoto ? (
            <div
              className="prop-bg-image"
              style={{ backgroundImage: `url(${imageSrc})` }}
            >
              <div
                className="prop-pen"
                style={{
                  backgroundColor: iconColor,
                  margin: "6px",
                  borderRadius: "4px",
                }}
              >
                {index + 1}
              </div>
            </div>
          ) : (
            <div className="prop-bg-image flex items-start">
              <div
                className="prop-pen"
                style={{
                  margin: "6px",
                  backgroundColor: iconColor,
                  borderRadius: "4px",
                }}
              >
                {index + 1}
              </div>
            </div>
          )}
          <div className="property-details">
            <div className="flex flex-col gap-y-1">
              <h3 className="fancy prop-title truncate">{displayTitle}</h3>
              <p className="prop-card-address line-clamp-2">
                {formatAddress()}
              </p>
            </div>
            {!isLeaseCompsSectionHidden && (
              <EmeraldButton
                label="See Details"
                type="Secondary"
                size="Small"
                onClick={(e) => {
                  e?.stopPropagation();
                  navigate(
                    `/interactive/${surveyId}/leasecomps?highlight=${encodeURIComponent(comp.leaseCompId)}`,
                  );
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

interface PropertySidebarProps {
  // State props
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  filteredProperties: Property[];
  properties: Property[];
  selectedProperty: Property | null;
  selectedLeaseCompId?: string | null;
  loadingState: {
    properties: boolean;
  };
  handleLeaseCompClick?: (leaseCompId: string) => void;

  // Event handlers
  handlePropertyClick: (property: Property) => void;
  updateFilter: (
    key: keyof MapFilters,
    value: string | boolean | [number, number] | null,
  ) => void;
}

const PropertySidebar = ({
  sidebarOpen,
  setSidebarOpen,
  filteredProperties,
  properties,
  selectedProperty,
  selectedLeaseCompId,
  loadingState,
  handleLeaseCompClick,
  handlePropertyClick,
  updateFilter,
}: PropertySidebarProps) => {
  const { surveyId } = useParams<{ surveyId: string }>();
  const { leaseComps: rawLeaseComps, fieldRules: leaseCompFieldRules } =
    useAppSelector((state) => state.leaseComps);
  const { leaseMediaFiles } = useAppSelector((state) => state.leaseMedia);
  const { currentSurvey } = useAppSelector((state) => state.interactiveSurvey);
  const leaseComps = currentSurvey?.isIncludeLeaseComps
    ? rawLeaseComps.filter((c) => !c.isHidden)
    : [];
  const showPropertyPhoto = currentSurvey?.showPropertyPhoto !== false;
  const isLeaseCompsSectionHidden =
    currentSurvey?.sections?.find((s) => s.name === "lease comps")?.isHidden ??
    false;

  const hasProperties = filteredProperties.length > 0;
  const hasLeaseComps = leaseComps.length > 0;

  // When there are no properties but there are lease comps, default to lease comps tab
  const [activeTab, setActiveTab] = useState<"properties" | "leaseComps">(
    !hasProperties && hasLeaseComps ? "leaseComps" : "properties",
  );

  // Switch to lease comps tab when a lease comp pin is clicked on the map
  useEffect(() => {
    if (selectedLeaseCompId) {
      setActiveTab("leaseComps");
    }
  }, [selectedLeaseCompId]);

  // Switch back to properties tab when a property pin is clicked on the map
  useEffect(() => {
    if (selectedProperty) {
      setActiveTab("properties");
    }
  }, [selectedProperty]);

  // Auto-switch to lease comps tab when properties disappear but lease comps exist
  useEffect(() => {
    if (!hasProperties && hasLeaseComps) {
      setActiveTab("leaseComps");
    }
  }, [hasProperties, hasLeaseComps]);

  // Close sidebar on screens smaller than 1024px
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };

    // Check on mount
    handleResize();

    // Add event listener
    window.addEventListener("resize", handleResize);

    // Cleanup event listener
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [setSidebarOpen]);

  return (
    <>
      {/* Desktop Property Sidebar */}
      <div
        className={`bg-white border-l border-gray-200 transition-all duration-300  ${
          sidebarOpen ? "w-120" : "w-0"
        } flex flex-col overflow-hidden`}
      >
        <div className="px-3 pt-2 pb-0 border-b border-gray-200">
          <button
            onClick={() => setSidebarOpen(false)}
            className="px-1 py-0.5 text-xs font-light flex items-center gap-1 mb-2"
            aria-label="Hide property list"
            style={{ color: "#012A2D" }}
          >
            Hide
            <svg
              className="w-3 h-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={4}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>

          {/* Tabs + Shortlist toggle on same row */}
          <div className="flex items-center justify-between">
            <div className="flex gap-0">
              {(hasProperties || !hasLeaseComps) && (
                <button
                  className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === "properties"
                      ? "border-cbre-green text-cbre-green"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                  onClick={() => setActiveTab("properties")}
                >
                  Properties ({filteredProperties.length})
                </button>
              )}
              {hasLeaseComps && (
                <button
                  className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === "leaseComps"
                      ? "border-cbre-green text-cbre-green"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                  onClick={() => setActiveTab("leaseComps")}
                >
                  Lease Comps ({leaseComps.length})
                </button>
              )}
            </div>

            {activeTab === "properties" && (
              <ShortlistFilterToggle
                onToggle={(showShortlistOnly) =>
                  updateFilter("showShortlistOnly", showShortlistOnly)
                }
                shortlistedCount={
                  properties.filter((p) => p.isClientShortlist && !p.isHidden)
                    .length
                }
              />
            )}
          </div>
        </div>

        <div className="prop-list">
          {activeTab === "properties" ? (
            loadingState.properties ? (
              <div className="p-4 text-center">
                <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                <p className="text-sm text-gray-500">Loading properties...</p>
              </div>
            ) : (
              <>
                {filteredProperties.map((property: Property) => {
                  return (
                    <div
                      key={property.id}
                      onClick={(e) => {
                        // Check if the click target is within an action button first
                        const target = e.target as HTMLElement;
                        const isActionButton =
                          target.closest("button") ||
                          target.closest(".emerald-button") ||
                          target.closest(".emerald-icon-button") ||
                          target.closest(".emerald-input") ||
                          target.closest(".emerald-side-sheet__backdrop") ||
                          target.closest("[data-action-button]") ||
                          target.tagName === "BUTTON";

                        // If clicking on an action button, let it handle its own logic
                        if (isActionButton) {
                          return;
                        }

                        // Close any existing property popups before opening a new one
                        const existingPopups =
                          document.querySelectorAll(".property-popup");
                        existingPopups.forEach((popup) => popup.remove());

                        // Handle property click
                        handlePropertyClick(property);
                      }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          // Check if the keydown originated from within an input field or button
                          const target = e.target as HTMLElement;
                          const isActionButton =
                            target.closest("button") ||
                            target.closest(".emerald-button") ||
                            target.closest(".emerald-icon-button") ||
                            target.closest(".emerald-input") ||
                            target.closest(".emerald-side-sheet__backdrop") ||
                            target.closest("[data-action-button]") ||
                            target.tagName === "BUTTON" ||
                            target.tagName === "INPUT" ||
                            target.tagName === "TEXTAREA";

                          // If in an input/button, don't prevent default or handle property click
                          if (isActionButton) {
                            return;
                          }

                          e.preventDefault();

                          // Check if any modal/side sheet is truly visible/active
                          const visibleModals =
                            document.querySelectorAll(".emerald-side-sheet")
                              .length > 0
                              ? Array.from(
                                  document.querySelectorAll(
                                    ".emerald-side-sheet",
                                  ),
                                ).some((el) => {
                                  const computedStyle =
                                    window.getComputedStyle(el);
                                  return (
                                    computedStyle.display !== "none" &&
                                    computedStyle.visibility !== "hidden" &&
                                    computedStyle.opacity !== "0" &&
                                    el.getAttribute("aria-hidden") !== "true"
                                  );
                                })
                              : false;

                          const hasActiveModal =
                            visibleModals ||
                            document.querySelector(".modal-overlay") ||
                            document.body.classList.contains("modal-open") ||
                            document.body.style.overflow === "hidden";

                          // If a modal/side sheet is open, don't handle property clicks
                          if (hasActiveModal) {
                            return;
                          }

                          handlePropertyClick(property);
                        }
                      }}
                      aria-label={`View ${property.propertyName || property.name || property.address1} on map`}
                    >
                      <PropertyCard
                        key={property.id}
                        property={property}
                        isSelected={selectedProperty?.id === property.id}
                        classStyleName="prop-card-row"
                        imageClassName="h-16"
                        showActions={true}
                      />
                    </div>
                  );
                })}
              </>
            )
          ) : (
            // Lease Comps tab
            <>
              {leaseComps.map((comp, index) => (
                <LeaseCompCard
                  key={comp.leaseCompId}
                  comp={comp}
                  index={index}
                  leaseCompFieldRules={leaseCompFieldRules}
                  leaseMediaFiles={leaseMediaFiles}
                  selectedLeaseCompId={selectedLeaseCompId}
                  showPropertyPhoto={showPropertyPhoto}
                  isLeaseCompsSectionHidden={isLeaseCompsSectionHidden}
                  surveyId={surveyId}
                  onLeaseCompClick={handleLeaseCompClick}
                />
              ))}
            </>
          )}
        </div>
      </div>

      {/* Sidebar Toggle */}
      {!sidebarOpen && (
        <div className="flex border-l border-gray-200 h-full flex-col">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 flex items-center justify-center text-gray-600 hover:text-gray-800 hover:bg-gray-50 transition-colors"
            aria-label="Show property list"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={4}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
        </div>
      )}
    </>
  );
};

export default PropertySidebar;
